import { FileText, StickyNote, Package, Eye, Inbox, Loader, X } from "lucide-react";
import { useState } from "react";
import type { AdminOrderListItem } from "./types";

interface OrderListingTableProps {
  orders: AdminOrderListItem[];
  loading: boolean;
  activeTab: string;
  tabLabel: string;
  onAction: (order: AdminOrderListItem) => void;
  onPrescriptionClick: (orderId: number, orderNumber: string) => void;
  currentPage?: number;
  pageSize?: number;
}

const OrderListingTable: React.FC<OrderListingTableProps> = ({
  orders,
  loading,
  activeTab,
  tabLabel,
  onAction,
  onPrescriptionClick,
  currentPage = 1,
  pageSize = 10,
}) => {
  const [pdfBillUrl, setPdfBillUrl] = useState<string | null>(null);

  const getActionForOrder = (order: AdminOrderListItem) => {
    if (order.orderType === "direct_with_prescription" && !order.hasPrescription) return "generate";
    if (order.orderType === "direct_with_prescription" && order.hasPrescription) return "approve";
    if (order.orderType === "prescription_based") return "prepare";
    return null;
  };

  const getActionLabel = (order: AdminOrderListItem) => {
    const action = getActionForOrder(order);
    if (action === "generate") return "Generate Prescription";
    if (action === "approve") return "Approve Order";
    if (action === "prepare") return "Prepare Order";
    return "";
  };

  const actionStyles: Record<string, string> = {
    generate: "bg-[#e8f0fe] border border-[#1a73e8] text-[#1a73e8] hover:bg-[#d2e3fc] hover:text-[#1557b0] transition-colors shadow-sm",
    approve: "bg-[#fff7e6] border border-[#f5a623] text-[#d97706] hover:bg-[#feeec8] hover:text-[#b45309] transition-colors shadow-sm",
    prepare: "bg-[#e6f7f4] border border-[#00a896] text-[#00a896] hover:bg-[#d1f2ec] hover:text-[#007d70] transition-colors shadow-sm",
  };

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });

  const getStatusLabel = (status: string) => {
    if (!status) return "—";
    return status.replace(/_/g, " ").toUpperCase();
  };

  const getStatusStyle = (status: string) => {
    if (!status) return "text-slate-500 bg-slate-50 border border-slate-200";
    const clean = status.toLowerCase();
    if (clean === "pending") return "text-amber-600 bg-amber-50 border border-amber-200";
    if (clean === "cancelled") return "text-rose-600 bg-rose-50 border border-rose-200";
    if (clean === "delivered") return "text-emerald-600 bg-emerald-50 border border-emerald-200";
    if (clean === "out_for_delivery") return "text-blue-600 bg-blue-50 border border-blue-200";
    return "text-indigo-600 bg-indigo-50 border border-indigo-200";
  };

  // Define dynamic headers based on tab
  const getHeaders = () => {
    if (activeTab === "return") {
      return ["S.No.", "Return ID", "Ordered By", "Clinic Details", "Products", "Address", "Amount", "Return Status", "Action"];
    }
    if (activeTab === "unprocessed") {
      return ["S.No.", "Ordered By", "Products", "Address", "Amount", "Status", "Action"];
    }
    if (activeTab === "ongoing") {
      return ["S.No.", "Order ID", "Ordered By", "Clinic Details", "Products", "Address", "Amount", "Current Status", "Action"];
    }
    if (activeTab === "completed") {
      return ["S.No.", "Order ID", "Ordered By", "Clinic Details", "Products", "Address", "Amount", "Current Status", "Action", "Bill"];
    }
    // Default / pending tab
    return ["S.No.", "Order ID", "Ordered By", "Ordered For", "Products", "Address", "Amount", "Rx", "Note", "Action"];
  };

  const headers = getHeaders();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden relative">
      <div className="overflow-x-auto">
        <table className="w-full text-[0.8rem] min-w-[900px]">
          <thead>
            <tr className="bg-slate-50 sticky top-0 z-[2]">
              {headers.map((h) => (
                <th
                  key={h}
                  className="px-3 py-2.5 text-left font-semibold text-slate-500 border-b border-slate-200 whitespace-nowrap uppercase text-[0.65rem] tracking-wider"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i}>
                  {headers.map((_, j) => (
                    <td key={j} className="px-3 py-3">
                      <div
                        className="h-3.5 rounded bg-gradient-to-r from-slate-100 via-slate-200 to-slate-100 animate-pulse"
                        style={{ width: j === 4 ? 120 : 70 }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={headers.length}>
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                      <Inbox size={24} />
                    </div>
                    <p className="text-sm font-semibold text-slate-500">
                      No {tabLabel.toLowerCase()} orders found
                    </p>
                  </div>
                </td>
              </tr>
            ) : (
              orders.map((order, orderIndex) => {
                const action = getActionForOrder(order);

                // Render dynamic columns for Return
                if (activeTab === "return") {
                  const returnStatusLabel = getStatusLabel((order as any).returnStatus);
                  const returnStatusStyle = getStatusStyle((order as any).returnStatus);
                  const returnTotal = (order as any).returnTotal ?? order.totalAmount;
                  const returnItemsCount = (order as any).returnItemsCount ?? order.productCount;

                  return (
                    <tr key={(order as any).returnId || order.orderId} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0">
                      {/* S.No. */}
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-medium text-slate-500">{(currentPage - 1) * pageSize + orderIndex + 1}</span>
                      </td>
                      {/* Return ID & Order ID */}
                      <td className="px-3 py-2.5">
                        <span className="font-extrabold text-emerald-600 text-xs">#{(order as any).returnId}</span>
                        <div className="text-[0.65rem] text-slate-400 mt-0.5">Order: #{order.orderNumber}</div>
                      </td>
                      {/* Ordered By */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 text-xs">{order.patientName}</div>
                        <div className="text-[0.65rem] text-slate-400">{order.patientPhone}</div>
                      </td>
                      {/* Clinic Details */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 text-xs">{order.clinicName || "—"}</div>
                        <div className="text-[0.65rem] text-slate-400">{order.clinicPhone || ""}</div>
                      </td>
                      {/* Products */}
                      <td className="px-3 py-2.5">
                        <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-semibold text-[0.7rem]">
                          {returnItemsCount} item{returnItemsCount !== 1 ? "s" : ""}
                        </span>
                      </td>
                      {/* Address */}
                      <td className="px-3 py-2.5 max-w-[160px]">
                        <div className="truncate text-xs text-slate-600" title={`${order.address}, ${order.city}, ${order.state} - ${order.pincode}`}>
                          {order.address}
                        </div>
                        <div className="text-[0.65rem] text-slate-400">{order.city}, {order.pincode}</div>
                      </td>
                      {/* Amount */}
                      <td className="px-3 py-2.5">
                        <span className="font-bold text-slate-800 text-xs">₹{returnTotal}</span>
                      </td>
                      {/* Return Status */}
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-1.5 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${returnStatusStyle}`}>
                          {returnStatusLabel}
                        </span>
                      </td>
                      {/* Action */}
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => onAction(order)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer shadow-sm"
                        >
                          <Eye size={12} />
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                }

                // Render dynamic columns for Unprocessed
                if (activeTab === "unprocessed") {
                  const statusLabel = order.orderStatus?.toLowerCase() === "pending" ? "Payment Pending" : getStatusLabel(order.orderStatus);
                  const statusStyle = getStatusStyle(order.orderStatus);

                  return (
                    <tr key={order.orderId} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0">
                      {/* S.No. */}
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-medium text-slate-500">{(currentPage - 1) * pageSize + orderIndex + 1}</span>
                      </td>
                      {/* Ordered By */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 text-xs">{order.patientName}</div>
                        <div className="text-[0.65rem] text-slate-400">{order.patientPhone}</div>
                      </td>
                      {/* Products */}
                      <td className="px-3 py-2.5">
                        <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-semibold text-[0.7rem]">
                          {order.productCount} item{order.productCount !== 1 ? "s" : ""}
                        </span>
                      </td>
                      {/* Address */}
                      <td className="px-3 py-2.5 max-w-[200px]">
                        <div className="truncate text-xs text-slate-600" title={`${order.address}, ${order.city}, ${order.state} - ${order.pincode}`}>
                          {order.address}
                        </div>
                        <div className="text-[0.65rem] text-slate-400">{order.city}, {order.pincode}</div>
                      </td>
                      {/* Amount */}
                      <td className="px-3 py-2.5">
                        <span className="font-bold text-slate-800 text-xs">₹{order.totalAmount}</span>
                      </td>
                      {/* Status */}
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-1.5 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${statusStyle}`}>
                          {statusLabel}
                        </span>
                      </td>
                      {/* Action */}
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => onAction(order)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer shadow-sm"
                        >
                          <Eye size={12} />
                          View Details
                        </button>
                      </td>
                    </tr>
                  );
                }

                // Render dynamic columns for Ongoing or Completed
                if (activeTab === "ongoing" || activeTab === "completed") {
                  const statusLabel = getStatusLabel(order.orderStatus);
                  const statusStyle = getStatusStyle(order.orderStatus);

                  return (
                    <tr key={order.orderId} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0">
                      {/* S.No. */}
                      <td className="px-3 py-2.5">
                        <span className="text-xs font-medium text-slate-500">{(currentPage - 1) * pageSize + orderIndex + 1}</span>
                      </td>
                      {/* Order ID */}
                      <td className="px-3 py-2.5">
                        <span className="font-bold text-emerald-600 text-xs">#{order.orderNumber}</span>
                        <div className="text-[0.65rem] text-slate-400 mt-0.5">{formatDate(order.orderDate)}</div>
                      </td>
                      {/* Ordered By */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 text-xs">{order.patientName}</div>
                        <div className="text-[0.65rem] text-slate-400">{order.patientPhone}</div>
                      </td>
                      {/* Clinic Details */}
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-slate-800 text-xs">{order.clinicName || "—"}</div>
                        <div className="text-[0.65rem] text-slate-400">{order.clinicPhone || ""}</div>
                      </td>
                      {/* Products */}
                      <td className="px-3 py-2.5">
                        <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-semibold text-[0.7rem]">
                          {order.productCount} item{order.productCount !== 1 ? "s" : ""}
                        </span>
                      </td>
                      {/* Address */}
                      <td className="px-3 py-2.5 max-w-[160px]">
                        <div className="truncate text-xs text-slate-600" title={`${order.address}, ${order.city}, ${order.state} - ${order.pincode}`}>
                          {order.address}
                        </div>
                        <div className="text-[0.65rem] text-slate-400">{order.city}, {order.pincode}</div>
                      </td>
                      {/* Amount */}
                      <td className="px-3 py-2.5">
                        <span className="font-bold text-slate-800 text-xs">₹{order.totalAmount}</span>
                      </td>
                      {/* Current Status */}
                      <td className="px-3 py-2.5">
                        <span className={`px-2 py-1.5 rounded-lg font-bold text-[0.62rem] tracking-wide inline-block ${statusStyle}`}>
                          {statusLabel}
                        </span>
                      </td>
                      {/* Action */}
                      <td className="px-3 py-2.5">
                        <button
                          onClick={() => onAction(order)}
                          className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg text-[0.7rem] font-bold bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200 transition-all cursor-pointer shadow-sm"
                        >
                          <Eye size={12} />
                          View Details
                        </button>
                      </td>
                      {/* Bill (Completed Tab only) */}
                      {activeTab === "completed" && (
                        <td className="px-3 py-2.5">
                          {order.bill ? (
                            <button
                              onClick={() => setPdfBillUrl(order.bill || null)}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.7rem] font-bold bg-emerald-50 text-emerald-600 border border-emerald-200 hover:bg-emerald-500 hover:text-white transition-all cursor-pointer shadow-sm"
                            >
                              <FileText size={12} />
                              View Bill
                            </button>
                          ) : (
                            <span className="text-slate-350 text-xs font-semibold">Not Generated</span>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                }

                // Default Pending Tab Columns
                return (
                  <tr key={order.orderId} className="hover:bg-slate-50/80 transition-colors border-b border-slate-50 last:border-b-0">
                    <td className="px-3 py-2.5">
                      <span className="text-xs font-medium text-slate-500">{(currentPage - 1) * pageSize + orderIndex + 1}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-semibold text-emerald-600 text-xs">#{order.orderNumber}</span>
                      <div className="text-[0.65rem] text-slate-400 mt-0.5">{formatDate(order.orderDate)}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-slate-800 text-xs">{order.patientName}</div>
                      <div className="text-[0.65rem] text-slate-400">{order.patientPhone}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="font-semibold text-slate-800 text-xs">{order?.orderedForName || order.patientName}</div>
                      <div className="text-[0.65rem] text-slate-400">{order?.orderedForPhone || order.patientPhone}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="bg-emerald-50 text-emerald-600 px-2 py-0.5 rounded-full font-semibold text-[0.7rem]">
                        {order.productCount} item{order.productCount !== 1 ? "s" : ""}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 max-w-[160px]">
                      <div className="truncate text-xs text-slate-600" title={`${order.address}, ${order.city}, ${order.state} - ${order.pincode}`}>
                        {order.address}
                      </div>
                      <div className="text-[0.65rem] text-slate-400">{order.city}, {order.pincode}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="font-bold text-slate-800 text-xs">₹{order.totalAmount}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {order.prescriptions > 0 ? (
                        <button
                          className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[0.7rem] font-semibold bg-emerald-50 text-emerald-600  border border-emerald-200 hover:bg-emerald-500 hover:text-white hover:scale-105 transition-all cursor-pointer"
                          onClick={() => onPrescriptionClick(order.orderId, order.orderNumber)}
                        >
                          <FileText size={11} />
                          {order.prescriptions}
                        </button>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {order.ordernote ? (
                        <div className="relative group inline-block">
                          <div className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[0.68rem] text-slate-500 bg-slate-100 border border-slate-200 max-w-[100px] truncate">
                            <StickyNote size={10} />
                            {order.ordernote.substring(0, 12)}
                            {order.ordernote.length > 12 ? "..." : ""}
                          </div>
                          <div className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 bg-slate-800 text-white px-3 py-2 rounded-lg text-xs leading-relaxed min-w-[180px] max-w-[280px] z-50 shadow-lg break-words animate-fadeUp">
                            {order.ordernote}
                            <div className="absolute top-full left-1/2 -translate-x-1/2 border-[5px] border-transparent border-t-slate-800" />
                          </div>
                        </div>
                      ) : (
                        <span className="text-slate-300 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {action ? (
                        <button
                          className={`inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold cursor-pointer transition-all hover:-translate-y-0.5 hover:shadow-md ${actionStyles[action] || ""}`}
                          onClick={() => onAction(order)}
                        >
                          {action === "generate" && <FileText size={14} />}
                          {action === "approve" && <Loader size={14} />}
                          {action === "prepare" && <Package size={14} />}
                          {getActionLabel(order)}
                        </button>
                      ) : (
                        <button
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.7rem] font-semibold bg-slate-100 text-slate-500 border border-slate-200 hover:bg-slate-200 transition-colors"
                          onClick={() => onAction(order)}
                        >
                          <Eye size={12} />
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Bill Preview PDF Modal */}
      {pdfBillUrl && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[200000] flex items-center justify-center p-4 animate-fadeIn" onClick={() => setPdfBillUrl(null)}>
          <div className="bg-white rounded-2xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-slideUp" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200 bg-white">
              <h3 className="text-sm font-bold text-slate-800">Order Invoice / Bill</h3>
              <button
                className="w-8 h-8 rounded-lg bg-slate-105 text-slate-500 flex items-center justify-center hover:bg-red-50 hover:text-red-500 transition-colors border-none cursor-pointer"
                onClick={() => setPdfBillUrl(null)}
              >
                <X size={16} />
              </button>
            </div>
            {/* Body */}
            <div className="flex-1 bg-slate-100">
              <iframe
                src={`${pdfBillUrl}#toolbar=0`}
                className="w-full h-full border-none"
                title="Invoice Bill PDF"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default OrderListingTable;
